import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { LegacyPageEvent as PageEvent } from "@angular/material/legacy-paginator";
import { Store, select } from "@ngrx/store";
import { isEqual } from "lodash";
import { BehaviorSubject, Observable, Subject, merge, EMPTY, of, filter, forkJoin } from "rxjs";
import { distinctUntilChanged, map, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { addDays } from "date-fns";

import { LegalEntityService } from "@shared/services/legal-entity/legal-entity.service";
import { LoaderService } from "@shared/services/loader/loader.service";
import { PaygroupsService } from "@shared/services/paygroups/paygroups.service";
import { Pagination } from "src/app/shared/models/pagination.interface";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import { getGlobalDashboardFilterState, getPagination, getUserDataState } from "src/app/store";
import { UpdateDashboardFilterAction } from "src/app/store/actions/globalDashboardFilterSelect.action";
import { AppState } from "src/app/store/models/state.model";
import {
	CalendarErrorMessageTitle,
	CalendarErrorRecordItem,
	CalendarErrorUserDetails,
	CardDetails,
	CustomerTableDetailItem,
	CustomerTableDetailWithPagination,
	DashboardFilters,
	EventsTableDetailItem,
	EventsTableDetailsWithPagination,
	FailedBeneficiariesTableDetailItem,
	FailedBeneficiariesTableDetailWithPagination,
	FailedPaymentOrdersTableDetailItem,
	FailedPaymentOrdersTableDetailWithPagination,
	FailedTransactionsTableDetailItem,
	FailedTransactionsTableDetailWithPagination,
	IDashboardFiltersFormGroup,
	LayoutType,
	LegalEntityDetailsWithPaygroups,
	MissingBankAccountsTableDetailItem,
	MissingBankAccountsTableDetailWithPagination,
	MissingCardTableDetailWithPagination,
	MissingCardsTableDetailItem,
	PayGroupDetails,
	PaygroupTableDetailItem,
	PaygroupTableDetailWithPagination
} from "../models/global-dashboard-interface";
import { DataFormattingService } from "../services/data-fomatting.service";
import { GlobalDashboardApiService } from "../services/global-dashboard-api.service";
import { HistoryLogSidePanelService } from "@shared/components/history-log-side-panel/services/history-log-side-panel.service";
import {
	HistoryLogType,
	MilestoneHistoryLogType
} from "@shared/components/history-log-side-panel/models/history-log-interface";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { LegalEntity } from "@shared/models/legal-entity.interface";
import { CountriesService } from "@shared/services/countries/countries.service";
import { Country } from "@shared/models/country.interface";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { HideEventsDialogComponent } from "../_components/hide-events-dialog/hide-events-dialog.component";
import { MilestoneUpdateIds } from "@shared/models/pay-cycle.interface";
import { MilestonesService } from "@shared/services/milestones/milestones.service";
import { PaginationService } from "@shared/components/pagination/_services/pagination.service";
import { PaginationOptions } from "@shared/constants/pagination";
import { stringArraysEqualIgnoreOrder } from "@shared/utils/array-helpers.util";
import { formatDateYYYYMMDD } from "@shared/utils/date.util";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";

@Component({
	selector: "app-global-dashboard",
	templateUrl: "./global-dashboard.component.html",
	styleUrls: ["./global-dashboard.component.scss"]
})
export class GlobalDashboardComponent implements OnInit, OnDestroy {
	/** Import declaration */
	private formBuilder = inject(FormBuilder);
	private globalDashboardApiService = inject(GlobalDashboardApiService);
	private store = inject(Store<AppState>);
	private legalEntityService = inject(LegalEntityService);
	private paygroupsService = inject(PaygroupsService);
	private cdr = inject(ChangeDetectorRef);
	private dataFormattingService = inject(DataFormattingService);
	private loaderService = inject(LoaderService);
	private historyLogService = inject(HistoryLogSidePanelService);
	private permissionsService = inject(PermissionsService);
	private countriesService = inject(CountriesService);
	private milestonesService = inject(MilestonesService);
	private dialog = inject(MatDialog);
	private paginationService = inject(PaginationService);
	private featureManagementService = inject(FeatureManagementService);

	todaysDate: Date = new Date();
	kpiCards: CardDetails[] = this.dataFormattingService.getKPIDefaultValue();
	showDefaultTableView = true;
	filters!: FormGroup;
	kpiSelected = "";
	pagination: Pagination = {
		pageSize: 10,
		pageIndex: 0,
		totalPageCount: 0
	};
	tableTitle = "";
	icon = "";
	dataLandingPageEvents!: EventsTableDetailItem[];
	dataLandingPageCustomers!: CustomerTableDetailItem[];
	dataLandingPagePaygroups!: PaygroupTableDetailItem[];
	dataMissingCards: MissingCardsTableDetailItem[] = [];
	dataMissingBankAccounts: MissingBankAccountsTableDetailItem[] = [];
	dataFailedBeneficiaries: FailedBeneficiariesTableDetailItem[] = [];
	dataFailedTransactions: FailedTransactionsTableDetailItem[] = [];
	dataFailedPaymentOrders: FailedPaymentOrdersTableDetailItem[] = [];
	customersSelectOptions = new BehaviorSubject<SelectOption[]>([]);
	legalEntitiesSelectOptions = new BehaviorSubject<SelectOption[]>([]);
	payGroupsSelectOptions = new BehaviorSubject<SelectOption[]>([]);
	groupSelectOptions = new BehaviorSubject<SelectOption[]>([]);
	statusSelectOptions = new BehaviorSubject<SelectOption[]>([]);
	filterPageSize = -1;
	filterPageNumber = 0;
	filterTotalCount = -1;
	fetchingDataTable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	fetchingDataForFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	noEntriesFound = false;
	encounteredErrorWhileFecthingData = false;
	showHistoryLogPanel = false;
	isClient = false;
	HistoryLogType = HistoryLogType;
	headerFlag = "";
	canSeeMap = false;
	countries: Country[] = [];
	clientSelectedCountry!: string;
	clientCountryList: SelectOption[] = [];
	clientCustomerId!: string;
	isClientGlobalDashboard = false;
	selectedEvents: EventsTableDetailItem[] = [];
	paginationType: PaginationOptions = PaginationOptions.GLOBAL_DASHBOARD;
	readonly LayoutType = LayoutType;
	private destroy$: Subject<void> = new Subject<void>();
	gdEventListPageSize = 10;

	/** Calendar Error */
	fetchCalendarErrorCounts = signal(true);
	calendarErrorCounts = 0;
	calendarErrorRecords: CalendarErrorRecordItem[] = [];
	calendarErrorUserEmail = "";
	calendarErrorTimeStamp = "";
	calendarErrorMinDate = "";
	calendarErrorLocalStorage: CalendarErrorUserDetails[] = [];
	isToasterClosed = true;
	private readonly CALENDAR_ERROR_USER_DETAILS = "CALENDAR_ERROR_USER_DETAILS";

	private isKPICardEnabled = false;

	constructor() {}

	ngOnInit(): void {
		this.countriesService
			.getCountries()
			.pipe(take(1))
			.subscribe({
				next: countries => {
					this.countries = countries;
				}
			});

		this.permissionsService
			.canSeeMapAndIsClient()
			.pipe(
				takeUntil(this.destroy$),
				distinctUntilChanged(
					(prev, curr) => prev.isClient === curr.isClient && prev.canSeeMap === curr.canSeeMap
				)
			)
			.subscribe((res: { isClient: boolean; canSeeMap: boolean }) => {
				this.canSeeMap = res.canSeeMap;
				this.isClient = res.isClient;
				this.setupGlobalDashboard();
			});

		this.permissionsService
			.enableClientGlobalFilter()
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				this.isClientGlobalDashboard = res.isClientGlobalDashboard!;
			});

		/** Create || Update local storage for calendar error user details */
		this.store
			.select(getUserDataState)
			.pipe(take(1))
			.subscribe(userDetails => {
				const user = {
					email: userDetails.email || "",
					userLastGlobalDashboardVisit: new Date().toISOString(),
					isToasterClosed: true,
					calendarErrorItems: []
				};
				this.calendarErrorUserEmail = userDetails.email || "";

				this.createUpdateCalendarErrorUserDetails(user);
				this.calendarErrorLocalStorage = JSON.parse(
					localStorage.getItem(this.CALENDAR_ERROR_USER_DETAILS) || "[]"
				);

				this.calendarErrorMinDate =
					this.calendarErrorLocalStorage.find(
						(user: CalendarErrorUserDetails) => user.email === userDetails.email
					)?.userLastGlobalDashboardVisit || new Date().toISOString();
				const maxDate = new Date().toISOString();

				this.getCalendarErrors(this.calendarErrorMinDate, maxDate, userDetails.email || "");
			});
	}

	setupGlobalDashboard(): void {
		this.initialiseForm();
		this.getSelectionOptions();
		this.setupStateListeners();
		this.setupHistoryLogSubscription();
	}

	initialiseForm(): void {
		this.filters = this.formBuilder.group({
			customers: new FormControl([]),
			legalEntities: new FormControl([]),
			paygroups: new FormControl([]),
			statuses: new FormControl([]),
			groups: new FormControl([]),
			deadline: new FormControl(""),
			milestoneStartDate: new FormControl(""),
			milestoneTypes: new FormControl([]),
			process: new FormGroup({
				automatic: new FormControl(false),
				manual: new FormControl(false)
			})
		}) as IDashboardFiltersFormGroup;

		this.filters.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(form => {
			this.filterChangeResetPagination();
			this.cdr.detectChanges(); //Prevents ExpressionChangedAfterItHasBeenCheckedError
			this.fetchingDataForFilters.next(true);
		});

		//Break up valueChanges so that all endpoints don't get run when chnaging one dropdown's value

		this.filters
			.get("customers")
			?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
			.subscribe(customerIds => {
				if (customerIds.length > 0) {
					this.legalEntityService
						.getLegalEntitiesForCustomersAsSelectOption(
							this.checkIfAllIsSelected([...customerIds], "customers")
						)
						.pipe(
							takeUntil(this.destroy$),
							map(legalEntities => {
								this.legalEntitiesSelectOptions.next(legalEntities);
							})
						)
						.subscribe();
				} else {
					this.legalEntitiesSelectOptions.next([]);
				}
			});

		this.filters
			.get("legalEntities")
			?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(stringArraysEqualIgnoreOrder))
			.subscribe(legalEntityIds => {
				if (legalEntityIds.length > 0) {
					this.paygroupsService
						.getPaygroupsByGlobalDasboardFiltersAsSelectOptions(
							this.checkIfFilterHasAllSelected(this.filters)
						)
						.pipe(
							takeUntil(this.destroy$),
							map(payGroups => {
								this.payGroupsSelectOptions.next(payGroups);
							})
						)
						.subscribe();
				} else {
					this.payGroupsSelectOptions.next([]);
				}
			});
	}

	/**
	 * Use to get client entities
	 * @param customerId: Client customer ID
	 */
	getClientLegalEntities(customerId: string): void {
		this.clientCustomerId = customerId;
		this.legalEntityService
			.getLegalEntitiesForCustomer(customerId)
			.pipe(take(1))
			.subscribe({
				next: res => {
					const legalEntities: LegalEntity[] = res.items;
					this.getClientCountryBreadcrumbInfo(legalEntities);
				}
			});
	}

	/**
	 * Get client-related country breadcrumb info
	 * @param legalEntities: Client legal entities
	 */
	getClientCountryBreadcrumbInfo(legalEntities: LegalEntity[]): void {
		this.clientCountryList = [];
		legalEntities.forEach(legalEntity => {
			if (legalEntity.payGroupCount == 0) return;

			const existingIndex: number = this.clientCountryList.findIndex(
				(country: SelectOption) => country.value === legalEntity.data.country
			);

			if (existingIndex === -1) {
				this.clientCountryList.push(
					this.countriesService.filterCountryByCode(this.countries, legalEntity.data.country)
				);

				this.clientCountryList[this.clientCountryList.length - 1].valueArray = [legalEntity.id];
			} else {
				this.clientCountryList[existingIndex].valueArray?.push(legalEntity.id);
			}
		});
		const selectedLegalEntityId = Array.isArray(this.filters.get("legalEntities")?.value)
			? this.filters.get("legalEntities")?.value[0]
			: this.filters.get("legalEntities")?.value;

		if (!this.isClientGlobalDashboard)
			this.headerFlag = this.legalEntitiesSelectOptions.value.filter(f => f.value == selectedLegalEntityId)[0]
				?.imagePath as "";
		else this.headerFlag = "";

		this.clientSelectedCountry = "";
		let clientSelectedCountryId = "";

		this.clientCountryList.forEach(legalEntity => {
			if (legalEntity.valueArray?.includes(selectedLegalEntityId)) {
				this.clientSelectedCountry = legalEntity.text || "";
				clientSelectedCountryId = legalEntity.value;
			}
		});
	}

	checkIfFilterHasAllSelected(filter: FormGroup<any>): DashboardFilters {
		const copiedFilter = filter.getRawValue(); // prevent manipulating the this.filters value

		const formKeys = Object.keys(copiedFilter);
		const endIndex = 5;

		for (let i = 0; i < endIndex; i++) {
			const key = formKeys[i];
			const keyValue = copiedFilter[key];

			if (
				keyValue.some(
					(value: string) =>
						value === "customers" ||
						// value === "legal entities" ||
						value === "paygroups" ||
						value === "statuses" ||
						value === "groups"
				)
			) {
				copiedFilter[key] = [];
			}
		}

		return copiedFilter;
	}
	checkIfAllIsSelected(selection: string[], allValue: string): string[] {
		return selection.includes(allValue) ? [""] : selection;
	}

	getSelectionOptions(): void {
		this.setCustomerSelectOptions();
		this.setGroupSelectOptions();
		this.setStatusSelectOptions();
	}

	setCustomerSelectOptions(): void {
		this.globalDashboardApiService
			.getCustomerSelectOptions(this.filterPageSize, this.filterPageNumber)
			.pipe(takeUntil(this.destroy$))
			.subscribe(customers => {
				this.customersSelectOptions.next(customers);
			});
	}

	setGroupSelectOptions(): void {
		this.groupSelectOptions.next(this.globalDashboardApiService.getGroupSelectionOptions());
	}

	setStatusSelectOptions(): void {
		this.statusSelectOptions.next(this.globalDashboardApiService.getStatusSelectionOptions());
	}

	setupStateListeners(): void {
		this.store
			.pipe(
				takeUntil(this.destroy$),
				select(getGlobalDashboardFilterState),
				distinctUntilChanged((a, b) => isEqual(a?.globalDashboard, b?.globalDashboard))
			)
			.subscribe(state => {
				if (state && state.globalDashboard && state.globalDashboard.filters) {
					if (this.kpiSelected !== state.globalDashboard.kpiSelected) {
						this.resetTableComponent();
						this.resetPagination();
						this.showDefaultTableView = true;
					}

					if (!isEqual(state.globalDashboard.filters, this.filters.getRawValue())) {
						this.filters.patchValue(state.globalDashboard.filters);
						if (this.isClient && !this.isClientGlobalDashboard)
							this.getClientLegalEntities(this.filters.get("customers")?.value[0]);
					}

					if (this.isClient && state.globalDashboard.kpiSelected === "Customers") {
						this.kpiSelected = "Paygroups";
					} else if (!this.isClient && state.globalDashboard.kpiSelected === "Paygroups") {
						this.kpiSelected = "Customers";
					} else {
						this.kpiSelected = state.globalDashboard.kpiSelected;
					}

					if (
						!this.filters.get("deadline") ||
						this.filters.get("deadline")?.value === "" ||
						!this.filters.get("milestoneStartDate") ||
						this.filters.get("milestoneStartDate")?.value === ""
					) {
						this.setInitialDeadlineDate();
					}

					this.applyFilters();
				} else {
					this.kpiSelected = "";
				}
			});

		//Hide blue spinner
		this.loaderService.visibility$
			.pipe(takeUntil(this.destroy$))
			.subscribe(showSpinner => (showSpinner ? this.loaderService.visibility$.next(false) : false));
	}

	setupHistoryLogSubscription(): void {
		this.historyLogService.milestoneEventHistoryLogSubject.pipe(takeUntil(this.destroy$)).subscribe({
			next: res => {
				if (res.milestoneId !== "") {
					this.showHistoryLogPanel = true;
				} else {
					this.showHistoryLogPanel = false;
				}
			}
		});
	}

	patchNewData(filters: DashboardFilters): void {
		if (!this.canSeeMap && !this.isClientGlobalDashboard) {
			/** Handling for Client user role */
			if (!isEqual(this.filters.getRawValue(), filters)) {
				this.filters.patchValue(filters);
			}
		}
	}

	applyFilters(): void {
		if (this.filters.get("customers")?.value.length > 0) {
			this.loadDataBasedOnKPI(this.kpiSelected);
			this.showDefaultTableView = false;
		} else {
			this.resetTableComponent();
			this.resetPagination();
			this.kpiCards = this.dataFormattingService.getKPIDefaultValue();
			this.showDefaultTableView = true;
		}
	}

	private setInitialDeadlineDate(): void {
		const defaultStartDate = formatDateYYYYMMDD(addDays(new Date(), -61));
		const defaultEndDate = formatDateYYYYMMDD(addDays(new Date(), 31));

		this.filters.get("milestoneStartDate")?.setValue(defaultStartDate);
		this.filters.get("deadline")?.setValue(defaultEndDate);
	}

	/**
	 * Trigger applyFilters() after a second when updating
	 * in global dashboard event list
	 */
	milestoneDropdownFilterUpdate(): void {
		this.fetchingDataForFilters.next(true);
		this.fetchingDataTable.next(true);
		setTimeout(() => {
			this.applyFilters();
		}, 1000);
	}

	selectEvents(events: EventsTableDetailItem[]): void {
		this.selectedEvents = events;
	}

	hideEvent(element?: EventsTableDetailItem): void {
		const dialogRef = this.dialog.open(HideEventsDialogComponent, {
			autoFocus: true,
			disableClose: true,
			panelClass: "hide-events-dialog-container",
			width: "90vw",
			maxWidth: "769px",
			height: "80vh",
			maxHeight: "409px",
			data: element ? 1 : this.selectedEvents.length
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) return;
			const selectedMilestones: MilestoneUpdateIds = { ids: [] };

			selectedMilestones.ids = element
				? [element.milestoneDetails.id]
				: this.selectedEvents.map(milestone => milestone.milestoneDetails.id);

			this.milestonesService.hideMilestones(selectedMilestones).subscribe({
				next: res => {
					this.milestoneDropdownFilterUpdate();
				}
			});
		});
	}

	getCardDetails(): Observable<CardDetails[]> {
		return this.featureManagementService.hasFeature(FeatureFlagCodes.FE_KPI_CARDS).pipe(
			take(1),
			switchMap(enabled =>
				enabled
					? this.globalDashboardApiService
							.getCardDetails(
								this.checkIfFilterHasAllSelected(this.filters),
								this.dataFormattingService.getAvailableFiltersLayers(this.kpiSelected)
							)
							.pipe(
								map(cards => {
									this.fetchingDataForFilters.next(false);
									this.kpiCards = cards;
									return this.kpiCards;
								})
							)
					: of([])
			)
		);
	}

	loadMissingCardsTableDetails(): Observable<MissingCardsTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getMissingCardsTableDetails(
				this.checkIfFilterHasAllSelected(this.filters),
				this.pagination.pageSize,
				this.pagination.pageIndex
			)
			.pipe(
				map(cards => {
					this.icon = "card";
					this.tableTitle = "Missing Cards";
					if ("failed" in cards && cards.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataMissingCards = [];
						return this.dataMissingCards;
					} else {
						cards = cards as MissingCardTableDetailWithPagination;
						this.pagination.totalPageCount = cards.totalCount;
						this.dataMissingCards = cards.items;
						this.noEntriesFound = this.showNoEntries(this.dataMissingCards.length);
						return this.dataMissingCards;
					}
				})
			);
	}

	loadMissingBankAccountsTableDetails(): Observable<MissingBankAccountsTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getMissingBankAccountsTableDetails(
				this.checkIfFilterHasAllSelected(this.filters),
				this.pagination.pageSize,
				this.pagination.pageIndex
			)
			.pipe(
				map(bankAccounts => {
					this.icon = "bank-figma";
					this.tableTitle = "Missing Bank Accounts";
					if ("failed" in bankAccounts && bankAccounts.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataMissingBankAccounts = [];
						return this.dataMissingBankAccounts;
					} else {
						bankAccounts = bankAccounts as MissingBankAccountsTableDetailWithPagination;
						this.pagination.totalPageCount = bankAccounts.totalCount;
						this.dataMissingBankAccounts = bankAccounts.items;
						this.noEntriesFound = this.showNoEntries(this.dataMissingBankAccounts.length);
						return this.dataMissingBankAccounts;
					}
				})
			);
	}

	loadFailedBeneficiariesTableDetails(): Observable<FailedBeneficiariesTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getFailedBeneficiariesTableDetails(
				this.checkIfFilterHasAllSelected(this.filters),
				this.pagination.pageSize,
				this.pagination.pageIndex
			)
			.pipe(
				map(failedBens => {
					this.icon = "profile-2user";
					this.tableTitle = "Failed beneficiaries";
					if ("failed" in failedBens && failedBens.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataFailedBeneficiaries = [];
						return this.dataFailedBeneficiaries;
					} else {
						failedBens = failedBens as FailedBeneficiariesTableDetailWithPagination;
						this.pagination.totalPageCount = failedBens.totalCount;
						this.dataFailedBeneficiaries = failedBens.items;
						this.noEntriesFound = this.showNoEntries(this.dataFailedBeneficiaries.length);
						return this.dataFailedBeneficiaries;
					}
				})
			);
	}

	loadFailedTransactionsTableDetails(): Observable<FailedTransactionsTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getFailedTransactionsTableDetails(
				this.checkIfFilterHasAllSelected(this.filters),
				this.pagination.pageSize,
				this.pagination.pageIndex
			)
			.pipe(
				map(failedTransactions => {
					this.icon = "wallet-add";
					this.tableTitle = "Failed Transactions";
					if ("failed" in failedTransactions && failedTransactions.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataFailedTransactions = [];
						return this.dataFailedTransactions;
					} else {
						failedTransactions = failedTransactions as FailedTransactionsTableDetailWithPagination;
						this.pagination.totalPageCount = failedTransactions.totalCount;
						this.dataFailedTransactions = this.dataFormattingService.formatFailedTransactions(
							failedTransactions.items
						);
						this.noEntriesFound = this.showNoEntries(this.dataFailedTransactions.length);
						return this.dataFailedTransactions;
					}
				})
			);
	}

	loadFailedPaymentOrdersTableDetails(): Observable<FailedPaymentOrdersTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getFailedPaymentOrdersTableDetails(
				this.checkIfFilterHasAllSelected(this.filters),
				this.pagination.pageSize,
				this.pagination.pageIndex
			)
			.pipe(
				map(failedPaymentOrders => {
					this.icon = "document-copy";
					this.tableTitle = "Failed Payment Orders";
					if ("failed" in failedPaymentOrders && failedPaymentOrders.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataFailedPaymentOrders = [];
						return this.dataFailedPaymentOrders;
					} else {
						failedPaymentOrders = failedPaymentOrders as FailedPaymentOrdersTableDetailWithPagination;
						this.pagination.totalPageCount = failedPaymentOrders.totalCount;
						this.dataFailedPaymentOrders = this.dataFormattingService.formatFailedPaymentOrders(
							failedPaymentOrders.items
						);
						this.noEntriesFound = this.showNoEntries(this.dataFailedPaymentOrders.length);
						return this.dataFailedPaymentOrders;
					}
				})
			);
	}

	loadEventTableDetails(): Observable<EventsTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getEventsTableDetails(
				this.checkIfFilterHasAllSelected(this.filters),
				this.pagination.pageSize,
				this.pagination.pageIndex
			)
			.pipe(
				map(response => {
					this.icon = "";
					this.tableTitle = "Events";
					if ("failed" in response && response.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataLandingPageEvents = [];
						return this.dataLandingPageEvents;
					} else {
						response = response as EventsTableDetailsWithPagination;
						this.pagination.totalPageCount = response.totalCount;
						this.dataLandingPageEvents = response.items;
						this.noEntriesFound = this.showNoEntries(this.dataLandingPageEvents.length);
						return this.dataLandingPageEvents;
					}
				})
			);
	}

	loadCustomerTableDetails(): Observable<CustomerTableDetailItem[]> {
		this.resetTableComponent();

		return this.globalDashboardApiService
			.getCustomerDetails(
				this.checkIfAllIsSelected([...this.filters.get("customers")?.value], "customers"),
				this.pagination.pageSize,
				this.pagination.pageIndex,
				this.filters.get("legalEntities")?.value,
				this.legalEntitiesSelectOptions.value,
				this.filters.get("paygroups")?.value,
				this.payGroupsSelectOptions.value
			)
			.pipe(
				map(response => {
					this.icon = "";
					this.tableTitle = "Customers";
					if ("failed" in response && response.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataLandingPageCustomers = [];
						return this.dataLandingPageCustomers;
					} else {
						response = response as CustomerTableDetailWithPagination;
						this.pagination.totalPageCount = response.totalCount;
						this.dataLandingPageCustomers = response.items;
						this.noEntriesFound = this.showNoEntries(this.dataLandingPageCustomers.length);
						return this.dataLandingPageCustomers;
					}
				})
			);
	}

	loadPaygroupTableDetails(): Observable<PaygroupTableDetailItem[]> {
		this.resetTableComponent();
		return this.globalDashboardApiService
			.getLegalEntitiesWithPagination(
				this.filters.get("customers")?.value,
				this.pagination.pageSize,
				this.pagination.pageIndex,
				this.filters.get("legalEntities")?.value,
				this.payGroupsSelectOptions.value,
				this.filters.get("paygroups")?.value
			)
			.pipe(
				map(response => {
					this.icon = "";
					this.tableTitle = "Paygroups";
					if ("failed" in response && response.failed) {
						this.encounteredErrorWhileFecthingData = true;
						this.dataLandingPagePaygroups = [];
						return this.dataLandingPagePaygroups;
					} else {
						response = response as PaygroupTableDetailWithPagination;
						this.pagination.totalPageCount = response.totalCount;
						this.dataLandingPagePaygroups = response.items;
						this.noEntriesFound = this.showNoEntries(this.dataLandingPagePaygroups.length);
						return this.dataLandingPagePaygroups;
					}
				})
			);
	}

	fetchLegalEntitiesData(customerId: string): void {
		this.globalDashboardApiService
			.getLegalEntities(
				customerId,
				-1,
				0,
				this.filters.get("legalEntities")?.value,
				this.payGroupsSelectOptions.value,
				this.filters.get("paygroups")?.value
			)
			.pipe(take(1))
			.subscribe((legalEntites: LegalEntityDetailsWithPaygroups[]) => {
				const index = this.dataLandingPageCustomers.findIndex(
					customerData => customerData.customerDetails.id === customerId
				);

				this.dataLandingPageCustomers[index].customerDetails.legalEntities = legalEntites;
			});
	}

	fetchPaygroupsDataForCustomerTable(eventData: [string, string]): void {
		const [customerId, legalEnitityId] = eventData;

		this.globalDashboardApiService
			.getPaygroups(legalEnitityId, this.filters.get("paygroups")?.value)
			.pipe(take(1))
			.subscribe((paygroups: PayGroupDetails[]) => {
				const customerIndex = this.dataLandingPageCustomers.findIndex(
					customerData => customerData.customerDetails.id === customerId
				);

				if (this.dataLandingPageCustomers[customerIndex].customerDetails.legalEntities) {
					const legalEntityIndex = this.dataLandingPageCustomers[
						customerIndex
					].customerDetails.legalEntities!.findIndex(legalEnitity => legalEnitity.id === legalEnitityId);

					this.dataLandingPageCustomers[customerIndex].customerDetails.legalEntities![
						legalEntityIndex
					].payGroups = paygroups;
				}
			});
	}

	fetchPaygroupsDataForPaygroupTable(eventData: [string, string]): void {
		const [customerId, legalEnitityId] = eventData;

		this.globalDashboardApiService
			.getPaygroups(legalEnitityId, this.filters.get("paygroups")?.value)
			.pipe(take(1))
			.subscribe((paygroups: PayGroupDetails[]) => {
				const legalEntityIndex = this.dataLandingPagePaygroups.findIndex(
					legalEnitityData => legalEnitityData.legalEntityDetails.id === legalEnitityId
				);
				this.dataLandingPagePaygroups[legalEntityIndex].legalEntityDetails.payGroups = paygroups;
			});
	}

	switchToCustomerTable(loadCustomer: boolean): void {
		if (loadCustomer) {
			this.isClient ? this.setSelectedKPI("Paygroups") : this.setSelectedKPI("Customers");
		} else {
			this.setSelectedKPI("");
		}
		this.applyFilters();
	}

	setSelectedKPI(kpi: string): void {
		if (kpi !== this.kpiSelected) {
			this.store.pipe(take(1), takeUntil(this.destroy$), select(getGlobalDashboardFilterState)).subscribe({
				next: state => {
					this.store.dispatch(
						new UpdateDashboardFilterAction({
							globalDashboard: {
								kpiSelected: kpi,
								filters: this.filters.getRawValue(),
								isExpand: state.globalDashboard.isExpand
							}
						})
					);
				}
			});

			this.applyFilters();
		}
	}

	loadDataBasedOnKPI(kpi: string): void {
		this.encounteredErrorWhileFecthingData = false;
		switch (kpi) {
			case "Missing Cards":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadMissingCardsTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;
			case "Failed Beneficiaries":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadFailedBeneficiariesTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;

			case "Missing Bank Acc.":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadMissingBankAccountsTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;

			case "Failed Transactions":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadFailedTransactionsTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;

			case "Failed Payment Orders":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadFailedPaymentOrdersTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;
			case "Customers":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadCustomerTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;
			case "Paygroups":
				this.fetchingDataTable.next(true);
				merge(
					this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY,
					this.loadPaygroupTableDetails()
				)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
				break;
			default:
				this.setupPaginationSubscription();
				this.fetchingDataTable.next(true);
				merge(this.fetchingDataForFilters.value ? this.getCardDetails() : EMPTY, this.loadEventTableDetails())
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.fetchingDataTable.next(false);
						}
					});
		}
	}

	selected(kpi: string): boolean {
		return this.kpiSelected === kpi;
	}

	showNoEntries(length: number): boolean {
		return length > 0 ? false : true;
	}

	paginate(pagination: PageEvent): void {
		this.pagination.pageSize = pagination.pageSize;
		this.pagination.pageIndex = pagination.pageIndex;
		if (this.kpiSelected === "") {
			this.paginationService.updatePaginationSate(this.paginationType, this.pagination);
		}
		this.loadDataBasedOnKPI(this.kpiSelected);
		this.getCalendarErrors(this.calendarErrorMinDate, new Date().toISOString(), this.calendarErrorUserEmail);
	}

	resetTableComponent(): void {
		this.globalDashboardApiService.cancelPendingRequest();
		this.encounteredErrorWhileFecthingData = false;
		this.dataLandingPageEvents = [];
		this.dataLandingPageCustomers = [];
		this.dataLandingPagePaygroups = [];
		this.dataMissingCards = [];
		this.dataMissingBankAccounts = [];
		this.dataFailedTransactions = [];
		this.dataFailedBeneficiaries = [];
		this.dataFailedPaymentOrders = [];
		this.selectedEvents = [];
		this.tableTitle = "";
		this.icon = "";
	}

	/**
	 * Retain users selected number of page in global dashboard event list
	 */
	private setupPaginationSubscription(): void {
		this.store.pipe(select(getPagination), takeUntil(this.destroy$)).subscribe(pagination => {
			if (pagination && pagination.hasOwnProperty("GLOBAL_DASHBOARD")) {
				const globalDashboardPagination = pagination["GLOBAL_DASHBOARD"];

				this.pagination = {
					...this.pagination,
					pageSize: globalDashboardPagination.pageSize
				};

				this.gdEventListPageSize = globalDashboardPagination.pageSize;
			}
		});
	}

	/**
	 * Resets pagination pageIndex when user update filters
	 */
	filterChangeResetPagination(): void {
		this.pagination = {
			...this.pagination,
			pageSize: this.gdEventListPageSize,
			pageIndex: 0
		};
	}

	resetPagination(): void {
		this.pagination = {
			pageSize: 10,
			pageIndex: 0,
			totalPageCount: 0
		};
	}

	/**
	 * Initializes or updates calendar error user details in local storage
	 * @param user: calendar error user details
	 */
	createUpdateCalendarErrorUserDetails(user: CalendarErrorUserDetails): void {
		if (!localStorage.getItem(this.CALENDAR_ERROR_USER_DETAILS)) {
			localStorage.setItem(this.CALENDAR_ERROR_USER_DETAILS, JSON.stringify([user]));
		} else {
			let existingUserList = JSON.parse(localStorage.getItem(this.CALENDAR_ERROR_USER_DETAILS) || "[]");
			const isUserExist = existingUserList.some(
				(existingUser: CalendarErrorUserDetails) => existingUser.email === user.email
			);

			if (!isUserExist) {
				existingUserList.push(user);
				localStorage.setItem(this.CALENDAR_ERROR_USER_DETAILS, JSON.stringify(existingUserList));
			} else {
				existingUserList = existingUserList.map((existingUser: CalendarErrorUserDetails) =>
					existingUser.email === user.email
						? {
								...user,
								userLastGlobalDashboardVisit: existingUser.userLastGlobalDashboardVisit,
								isToasterClosed: existingUser.isToasterClosed,
								calendarErrorItems: existingUser.calendarErrorItems
						  }
						: existingUser
				);
				localStorage.setItem(this.CALENDAR_ERROR_USER_DETAILS, JSON.stringify(existingUserList));
			}
		}
	}

	/**
	 * Fetch calendar error counts and list for the user
	 * @param userEmail
	 */
	getCalendarErrors(minDate: string, maxDate: string, userEmail: string): void {
		this.fetchCalendarErrorCounts.set(true);
		this.featureManagementService
			.hasFeature(FeatureFlagCodes.CALENDAR_ERROR)
			.pipe(
				take(1),
				switchMap(enabled => {
					if (!enabled || this.isClient) {
						return EMPTY;
					}

					return this.globalDashboardApiService.getCalendarErrorCount(minDate, maxDate).pipe(take(1));
				}),
				tap(count => {
					if (count.found === 0) {
						this.calendarErrorCounts = 0;
						this.calendarErrorRecords = [];
						this.historyLogService.milestoneHistoryLogCalendarErrorItems.set([]);
						this.fetchCalendarErrorCounts.set(false);
						this.storeAndCheckCalendarErrorNewItems(this.calendarErrorUserEmail, []);
					}
				}),
				filter(count => count.found > 0),
				switchMap(errorCount =>
					this.globalDashboardApiService
						.getCalendarErrorList(minDate, maxDate, 100, this.pagination.pageIndex + 1)
						.pipe(
							take(1),
							map(errorList => ({
								errorCount,
								errorList
							}))
						)
				)
			)
			.subscribe(res => {
				this.calendarErrorCounts = res.errorCount.found;
				this.calendarErrorTimeStamp = maxDate;
				this.fetchCalendarErrorCounts.set(false);

				this.getCalendarErrorItemList(res.errorList.records);
			});
	}

	/**
	 * Remodel calendar error list items
	 * @param calendarErrorList: calendar error items from response
	 */
	getCalendarErrorItemList(calendarErrorList: any[]): void {
		this.calendarErrorRecords = calendarErrorList.map((record: any) => ({
			id: record.id,
			customerName: record.orgStructure?.customer?.name || "",
			customerId: record.orgStructure?.customer?.id || "",
			createdAt: record.createdAt,
			title: this.getCalendarErrorTitle(record.message),
			flag: "",
			subtitle:
				record.attributes?.CATEGORY === "MILESTONE"
					? `${record.orgStructure?.customer?.name || ""}. ID: ${
							record.attributes?.EXTERNAL_CUSTOMER_ID || ""
					  } - ${record.attributes?.EXTERNAL_LEGAL_ENTITY_ID || ""} - ${
							record.attributes?.EXTERNAL_PAY_GROUP_ID || ""
					  } / ${record.orgStructure?.payCycle?.name || ""}. ID: ${
							record.orgStructure?.payCycle?.cycleId || ""
					  } / ${
							(record.attributes?.PAYMENT_TYPE ?? "").toLowerCase() === "nets"
								? "NET"
								: record.attributes?.PAYMENT_TYPE ?? ""
					  } PAYMENTS`
					: "System configuration / Calendar setup",
			errorDescription: record.message,
			errorType: record.attributes?.CATEGORY === "MILESTONE" ? "Milestone error" : "Configuration error",
			hasCalendarLink: record.attributes?.CATEGORY === "MILESTONE",
			legalEntityId: record.orgStructure?.legalEntity?.id,
			payGroupId: record.orgStructure?.payGroup?.id,
			payCycleId: record.orgStructure?.payCycle?.id,
			milestoneGroup: record.attributes?.PAYMENT_TYPE || "",
			historyLogType: MilestoneHistoryLogType.CALENDAR_ERROR
		}));

		this.getCalendarErrorItemFlags(this.calendarErrorRecords);
	}

	/**
	 * Fetches and add flags for each calendar error items
	 * @param calendarErrorList: mapped calendar error list items
	 */
	getCalendarErrorItemFlags(calendarErrorList: CalendarErrorRecordItem[]): void {
		const legalEntityIds = [...new Set(calendarErrorList.map(item => item.legalEntityId))];

		forkJoin(legalEntityIds.map(id => this.legalEntityService.getLegalEntity(id!)))
			.pipe(take(1))
			.subscribe(res => {
				const countryFlags = new Map(res.map(legalEntity => [legalEntity.id, legalEntity.data.country]));

				this.calendarErrorRecords = this.calendarErrorRecords.map(item => ({
					...item,
					flag: countryFlags.get(item.legalEntityId!) || ""
				}));

				this.historyLogService.milestoneHistoryLogCalendarErrorItems.set(this.calendarErrorRecords);

				this.storeAndCheckCalendarErrorNewItems(this.calendarErrorUserEmail, this.calendarErrorRecords);
			});
	}

	/**
	 * Checked calendar error message to return error title
	 * @param errorMessage: calendar error message from response
	 * @returns calendar error title based on the error message
	 */
	getCalendarErrorTitle(errorMessage: string): string {
		const calendarErrorMessageTitle = [
			{
				includedTerms: ["Payment date", "missing"],
				errorMessageTitle: CalendarErrorMessageTitle.MISSING_PAYMENT_DATE,
				isIncluded: false
			},
			{
				includedTerms: ["Milestones", "not", "order"],
				errorMessageTitle: CalendarErrorMessageTitle.INCORRECT_ORDER,
				isIncluded: false
			},
			{
				includedTerms: ["Pay", "cycle", "not", "found"],
				errorMessageTitle: CalendarErrorMessageTitle.PAYCYCLE_NOT_FOUND,
				isIncluded: false
			},
			{
				includedTerms: ["Customer", "not", "found"],
				errorMessageTitle: CalendarErrorMessageTitle.CUSTOMER_NOT_FOUND,
				isIncluded: false
			},
			{
				includedTerms: ["Pay", "cycle", "not", "match"],
				errorMessageTitle: CalendarErrorMessageTitle.PAYCYCLE_NOT_MATCH,
				isIncluded: false
			},
			{
				includedTerms: ["Milestones", "can", "not", "update"],
				errorMessageTitle: CalendarErrorMessageTitle.MILESTONE_CANT_BE_UPDATED,
				isIncluded: false
			},
			{
				includedTerms: ["Milestone", "not", "possible", "update"],
				errorMessageTitle: CalendarErrorMessageTitle.MILESTONE_CANT_BE_UPDATED,
				isIncluded: false
			},
			{
				includedTerms: ["Go live", "missing"],
				errorMessageTitle: CalendarErrorMessageTitle.GO_LIVE_DATE_MISSING,
				isIncluded: false
			}
		];

		calendarErrorMessageTitle.forEach(term => {
			term.isIncluded = term.includedTerms.every(t => errorMessage.toLowerCase().includes(t.toLowerCase()));
		});

		return (
			calendarErrorMessageTitle.find(term => term.isIncluded)?.errorMessageTitle ||
			"There's an error in the calendar"
		);
	}

	storeAndCheckCalendarErrorNewItems(userEmail: string, calendarErrorItems: CalendarErrorRecordItem[]): void {
		const currentUserLocalStorageData = this.calendarErrorLocalStorage.find(
			(user: CalendarErrorUserDetails) => user.email === userEmail
		);
		const calendarErrorItemIds = new Set(calendarErrorItems.map(item => item.id));
		const existingCalendarErrorItemIds = new Set(
			currentUserLocalStorageData?.calendarErrorItems.map(item => item.id)
		);

		const hasNewCalendarErrorItems = Array.from(calendarErrorItemIds).some(
			id => !existingCalendarErrorItemIds.has(id)
		);

		if (hasNewCalendarErrorItems) {
			this.calendarErrorLocalStorage = this.calendarErrorLocalStorage.map((user: CalendarErrorUserDetails) =>
				user.email === userEmail
					? {
							...user,
							isToasterClosed: user.isToasterClosed === true ? false : user.isToasterClosed,
							calendarErrorItems: calendarErrorItems
					  }
					: user
			);
		} else {
			this.calendarErrorLocalStorage = this.calendarErrorLocalStorage.map((user: CalendarErrorUserDetails) =>
				user.email === userEmail ? { ...user, calendarErrorItems: calendarErrorItems } : user
			);
		}

		localStorage.setItem(this.CALENDAR_ERROR_USER_DETAILS, JSON.stringify(this.calendarErrorLocalStorage));
		this.isToasterClosed =
			this.calendarErrorLocalStorage.find((user: CalendarErrorUserDetails) => user.email === userEmail)
				?.isToasterClosed ||
			false ||
			this.calendarErrorCounts === 0;
	}

	/**
	 * Updates user last visit time in Global Dashboard
	 * when user leaves the page
	 * @param userEmail
	 */
	updateCalendarErrorUserLastVisitTime(userEmail: string): void {
		this.calendarErrorLocalStorage = JSON.parse(localStorage.getItem(this.CALENDAR_ERROR_USER_DETAILS) || "[]").map(
			(user: CalendarErrorUserDetails) =>
				user.email === userEmail ? { ...user, userLastGlobalDashboardVisit: new Date().toISOString() } : user
		);
		localStorage.setItem(this.CALENDAR_ERROR_USER_DETAILS, JSON.stringify(this.calendarErrorLocalStorage));
	}

	ngOnDestroy(): void {
		this.updateCalendarErrorUserLastVisitTime(this.calendarErrorUserEmail);
		this.destroy$.next();
		this.destroy$.complete();
	}
}
